/**
 * Agent Configuration Editor Component
 * Comprehensive editor for agent Azure AI Foundry settings and system prompt
 */

import { useState, useEffect } from 'react';
import { X, AlertCircle, CheckCircle, Eye, EyeOff, Save, Plus, Trash2, Sparkles, MessageSquare, Search } from 'lucide-react';
import { EmojiPicker, MarkdownEditor } from '@/components/Common';
import type {
  Agent,
  AgentSuggestedPrompt,
  AgentWelcomeMessage,
  AgentReasoningConfig,
  ReasoningEffort,
} from '@/types/agent.types';
import type {
  AgentWebSearchConfig,
  WebSearchProvider,
  TavilySearchConfig,
  BingSearchConfig,
  GoogleSearchConfig,
  SerpApiSearchConfig,
  BraveSearchConfig,
} from '@/types/webSearch.types';
import type { AzureAISearchConfig } from '@/types/aiSearch.types';
import {
  TavilyConfigForm,
  BingConfigForm,
  GoogleConfigForm,
  SerpApiConfigForm,
  BraveConfigForm,
  WebSearchDefaultStateField,
} from './WebSearchConfigForms';
import { AISearchConfigForm } from './AISearchConfigForm';

interface AgentConfigurationEditorProps {
  agent: Agent;
  onSave: (
    agentId: string,
    updates: {
      name?: string;
      description?: string;
      config?: Agent['config'];
      systemPrompt?: string;
      suggestedPrompts?: AgentSuggestedPrompt[];
      icon?: string;
      welcomeMessage?: AgentWelcomeMessage;
      webSearchConfig?: AgentWebSearchConfig;
      aiSearchConfig?: AzureAISearchConfig;
    }
  ) => Promise<void>;
  onClose: () => void;
}

const DEFAULT_SYSTEM_PROMPT = `You are a helpful AI assistant. Provide clear,
accurate, and concise responses. Format your answers using markdown for better
readability. When you don't know something, say so honestly.`;

export function AgentConfigurationEditor({
  agent,
  onSave,
  onClose,
}: AgentConfigurationEditorProps) {
  // Azure AI configuration state
  const [endpoint, setEndpoint] = useState('');
  const [apiKey, setApiKey] = useState('');
  const [deploymentId, setDeploymentId] = useState('');
  const [apiVersion, setApiVersion] = useState('');
  // Input context window (tokens). Stored as a string for the text input;
  // parsed to a positive integer (or undefined) on save.
  const [contextWindowTokens, setContextWindowTokens] = useState('');
  // What to do when the conversation exceeds the context window.
  const [contextOverflowStrategy, setContextOverflowStrategy] = useState<
    'trim' | 'summarize'
  >('trim');
  // Optional dedicated model for the 'summarize' strategy. All fields are
  // strings for the form; only `deployment` is required to enable it, the
  // rest default to the agent's own Azure AI settings when left blank.
  const [summaryModel, setSummaryModel] = useState({
    deployment: '',
    endpoint: '',
    apiKey: '',
    apiVersion: '',
  });
  const [showApiKey, setShowApiKey] = useState(false);

  // Reasoning / advanced-thinking configuration state (Azure AI agents only)
  const [supportsReasoning, setSupportsReasoning] = useState(false);
  const [reasoningOnEffort, setReasoningOnEffort] =
    useState<ReasoningEffort>('high');
  const [reasoningDefaultEnabled, setReasoningDefaultEnabled] =
    useState(false);
  const [supportsVision, setSupportsVision] = useState(false);

  // Agent metadata state
  const [name, setName] = useState(agent.name);
  const [description, setDescription] = useState(agent.description);
  const [icon, setIcon] = useState(agent.icon || '🤖');

  // System prompt state
  const [systemPrompt, setSystemPrompt] = useState(
    agent.systemPrompt || DEFAULT_SYSTEM_PROMPT
  );

  // Suggested prompts state
  const [suggestedPrompts, setSuggestedPrompts] = useState<
    AgentSuggestedPrompt[]
  >(agent.suggestedPrompts || []);

  // Welcome message state
  const [welcomeMessage, setWelcomeMessage] = useState<AgentWelcomeMessage>(
    agent.welcomeMessage || {
      useCustomMessage: false,
      title: '',
      subtitle: '',
    }
  );

  // Web search configuration state
  const [webSearchConfig, setWebSearchConfig] = useState<AgentWebSearchConfig>(
    agent.webSearchConfig || {
      enabled: false,
      provider: 'none',
    }
  );

  // AI Search configuration state
  const [aiSearchConfig, setAISearchConfig] = useState<AzureAISearchConfig>(
    agent.aiSearchConfig || {
      enabled: false,
      endpoint: '',
      apiKey: '',
      indexName: '',
      top: 5,
      useSemanticRanking: true,
      useVectorSearch: false,
    }
  );

  // UI state
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [activeTab, setActiveTab] = useState<'details' | 'azure' | 'prompt' | 'suggested' | 'welcome' | 'websearch' | 'aisearch'>('details');

  // Load initial values from agent config
  useEffect(() => {
    if (agent.config.framework === 'azure-ai-agent') {
      setEndpoint(agent.config.config.endpoint || '');
      setApiKey(agent.config.config.apiKey || '');
      setDeploymentId(agent.config.config.deploymentId || '');
      setApiVersion(agent.config.config.apiVersion || '2024-02-15-preview');
      const rc = agent.config.config.reasoningConfig;
      setSupportsReasoning(rc?.supportsReasoning ?? false);
      setReasoningOnEffort(rc?.onEffort ?? 'high');
      setReasoningDefaultEnabled(rc?.defaultEnabled ?? false);
      setSupportsVision(agent.config.config.supportsVision ?? false);
      setContextWindowTokens(
        agent.config.config.contextWindowTokens != null
          ? String(agent.config.config.contextWindowTokens)
          : ''
      );
      setContextOverflowStrategy(
        agent.config.config.contextOverflowStrategy ?? 'trim'
      );
      const sm = agent.config.config.summaryModel;
      setSummaryModel({
        deployment: sm?.deployment ?? '',
        endpoint: sm?.endpoint ?? '',
        apiKey: sm?.apiKey ?? '',
        apiVersion: sm?.apiVersion ?? '',
      });
    }
  }, [agent]);

  // Track changes
  useEffect(() => {
    const nameChanged = name !== agent.name;
    const descriptionChanged = description !== agent.description;
    const iconChanged = icon !== (agent.icon || '🤖');
    const promptChanged = systemPrompt !== (agent.systemPrompt || '');

    const promptsChanged = JSON.stringify(suggestedPrompts) !==
      JSON.stringify(agent.suggestedPrompts || []);

    const welcomeChanged = JSON.stringify(welcomeMessage) !==
      JSON.stringify(agent.welcomeMessage || {
        useCustomMessage: false,
        title: '',
        subtitle: '',
      });

    const webSearchChanged = JSON.stringify(webSearchConfig) !==
      JSON.stringify(agent.webSearchConfig || {
        enabled: false,
        provider: 'none',
      });

    const aiSearchChanged = JSON.stringify(aiSearchConfig) !==
      JSON.stringify(agent.aiSearchConfig || {
        enabled: false,
        endpoint: '',
        apiKey: '',
        indexName: '',
        top: 5,
        useSemanticRanking: true,
        useVectorSearch: false,
      });

    let configChanged = false;
    if (agent.config.framework === 'azure-ai-agent') {
      const rc = agent.config.config.reasoningConfig;
      const reasoningChanged =
        supportsReasoning !== (rc?.supportsReasoning ?? false) ||
        reasoningOnEffort !== (rc?.onEffort ?? 'high') ||
        reasoningDefaultEnabled !== (rc?.defaultEnabled ?? false);
      const visionChanged =
        supportsVision !== (agent.config.config.supportsVision ?? false);
      const storedWindow = agent.config.config.contextWindowTokens;
      const windowChanged =
        contextWindowTokens.trim() !==
        (storedWindow != null ? String(storedWindow) : '');
      const overflowStrategyChanged =
        contextOverflowStrategy !==
        (agent.config.config.contextOverflowStrategy ?? 'trim');
      const assembledSummary = summaryModel.deployment.trim()
        ? {
            deployment: summaryModel.deployment.trim(),
            ...(summaryModel.endpoint.trim()
              ? { endpoint: summaryModel.endpoint.trim() }
              : {}),
            ...(summaryModel.apiKey.trim()
              ? { apiKey: summaryModel.apiKey.trim() }
              : {}),
            ...(summaryModel.apiVersion.trim()
              ? { apiVersion: summaryModel.apiVersion.trim() }
              : {}),
          }
        : undefined;
      const summaryModelChanged =
        JSON.stringify(assembledSummary ?? null) !==
        JSON.stringify(agent.config.config.summaryModel ?? null);
      configChanged =
        endpoint !== (agent.config.config.endpoint || '') ||
        apiKey !== (agent.config.config.apiKey || '') ||
        deploymentId !== (agent.config.config.deploymentId || '') ||
        apiVersion !== (agent.config.config.apiVersion || '2024-02-15-preview') ||
        reasoningChanged ||
        visionChanged ||
        windowChanged ||
        overflowStrategyChanged ||
        summaryModelChanged;
    }

    setHasChanges(
      nameChanged ||
        descriptionChanged ||
        configChanged ||
        iconChanged ||
        promptChanged ||
        promptsChanged ||
        welcomeChanged ||
        webSearchChanged ||
        aiSearchChanged
    );
  }, [
    name,
    description,
    endpoint,
    apiKey,
    deploymentId,
    apiVersion,
    supportsReasoning,
    reasoningOnEffort,
    reasoningDefaultEnabled,
    supportsVision,
    contextWindowTokens,
    contextOverflowStrategy,
    summaryModel,
    icon,
    systemPrompt,
    suggestedPrompts,
    welcomeMessage,
    webSearchConfig,
    aiSearchConfig,
    agent,
  ]);

  const handleSave = async () => {
    setError(null);
    setSuccess(false);

    // Validation
    if (!name.trim()) {
      setError('Agent name is required');
      return;
    }

    if (!description.trim()) {
      setError('Agent description is required');
      return;
    }

    if (agent.config.framework === 'azure-ai-agent') {
      if (!endpoint.trim()) {
        setError('Azure AI Foundry endpoint is required');
        return;
      }

      if (!apiKey.trim()) {
        setError('API key is required');
        return;
      }

      if (!deploymentId.trim()) {
        setError('Deployment ID is required');
        return;
      }
    }

    if (!systemPrompt.trim()) {
      setError('System prompt cannot be empty');
      return;
    }

    try {
      setIsSaving(true);

      const updates: {
        name?: string;
        description?: string;
        config?: Agent['config'];
        systemPrompt?: string;
        suggestedPrompts?: AgentSuggestedPrompt[];
        icon?: string;
        welcomeMessage?: AgentWelcomeMessage;
        webSearchConfig?: AgentWebSearchConfig;
        aiSearchConfig?: AzureAISearchConfig;
      } = {};

      // Update name and description
      updates.name = name.trim();
      updates.description = description.trim();

      // Update Azure AI config
      if (agent.config.framework === 'azure-ai-agent') {
        const reasoningConfig: AgentReasoningConfig = {
          supportsReasoning,
          onEffort: reasoningOnEffort,
          defaultEnabled: reasoningDefaultEnabled,
        };
        // Parse the context window: keep only a positive integer; blank or
        // invalid input clears the override (falls back to the runtime default).
        const parsedWindow = contextWindowTokens.trim()
          ? Math.trunc(Number(contextWindowTokens.trim()))
          : NaN;
        const contextWindowValue =
          Number.isFinite(parsedWindow) && parsedWindow > 0
            ? parsedWindow
            : undefined;
        updates.config = {
          framework: 'azure-ai-agent',
          config: {
            endpoint: endpoint.trim(),
            apiKey: apiKey.trim(),
            deploymentId: deploymentId.trim(),
            apiVersion: apiVersion.trim() || '2024-02-15-preview',
            reasoningConfig,
            supportsVision,
            ...(contextWindowValue
              ? { contextWindowTokens: contextWindowValue }
              : {}),
            // Only persist a non-default strategy so untouched agents stay clean.
            ...(contextOverflowStrategy !== 'trim'
              ? { contextOverflowStrategy }
              : {}),
            // Persist the summary model only when a deployment is set;
            // endpoint/key/version are included only when non-blank (they
            // default to the agent's own settings at runtime).
            ...(summaryModel.deployment.trim()
              ? {
                  summaryModel: {
                    deployment: summaryModel.deployment.trim(),
                    ...(summaryModel.endpoint.trim()
                      ? { endpoint: summaryModel.endpoint.trim() }
                      : {}),
                    ...(summaryModel.apiKey.trim()
                      ? { apiKey: summaryModel.apiKey.trim() }
                      : {}),
                    ...(summaryModel.apiVersion.trim()
                      ? { apiVersion: summaryModel.apiVersion.trim() }
                      : {}),
                  },
                }
              : {}),
          },
        };
      }

      // Update agent icon
      updates.icon = icon;

      // Update system prompt
      updates.systemPrompt = systemPrompt.trim();

      // Update suggested prompts
      updates.suggestedPrompts = suggestedPrompts;

      // Update welcome message
      updates.welcomeMessage = welcomeMessage;

      // Update web search config
      updates.webSearchConfig = webSearchConfig;

      // Update AI search config
      updates.aiSearchConfig = aiSearchConfig;

      await onSave(agent.id, updates);

      setSuccess(true);
      setHasChanges(false);

      // Close after brief success message
      setTimeout(() => {
        onClose();
      }, 1500);
    } catch (err) {
      console.error('Failed to save configuration:', err);
      setError(
        err instanceof Error ? err.message : 'Failed to save configuration'
      );
    } finally {
      setIsSaving(false);
    }
  };

  const handleResetPrompt = () => {
    if (
      confirm('Reset system prompt to default? Your custom prompt will be lost.')
    ) {
      setSystemPrompt(DEFAULT_SYSTEM_PROMPT);
    }
  };

  const handleRevert = () => {
    // Revert to original values
    setName(agent.name);
    setDescription(agent.description);
    setIcon(agent.icon || '🤖');
    if (agent.config.framework === 'azure-ai-agent') {
      setEndpoint(agent.config.config.endpoint || '');
      setApiKey(agent.config.config.apiKey || '');
      setDeploymentId(agent.config.config.deploymentId || '');
      setApiVersion(agent.config.config.apiVersion || '2024-02-15-preview');
      const rc = agent.config.config.reasoningConfig;
      setSupportsReasoning(rc?.supportsReasoning ?? false);
      setReasoningOnEffort(rc?.onEffort ?? 'high');
      setReasoningDefaultEnabled(rc?.defaultEnabled ?? false);
      setSupportsVision(agent.config.config.supportsVision ?? false);
      setContextWindowTokens(
        agent.config.config.contextWindowTokens != null
          ? String(agent.config.config.contextWindowTokens)
          : ''
      );
      setContextOverflowStrategy(
        agent.config.config.contextOverflowStrategy ?? 'trim'
      );
      const sm = agent.config.config.summaryModel;
      setSummaryModel({
        deployment: sm?.deployment ?? '',
        endpoint: sm?.endpoint ?? '',
        apiKey: sm?.apiKey ?? '',
        apiVersion: sm?.apiVersion ?? '',
      });
    }
    setSystemPrompt(agent.systemPrompt || DEFAULT_SYSTEM_PROMPT);
    setSuggestedPrompts(agent.suggestedPrompts || []);
    setWelcomeMessage(agent.welcomeMessage || {
      useCustomMessage: false,
      title: '',
      subtitle: '',
    });
    setHasChanges(false);
    setError(null);
  };

  // Suggested prompts handlers
  const handleAddPrompt = () => {
    const newPrompt: AgentSuggestedPrompt = {
      title: '',
      prompt: '',
      icon: '💡',
      category: 'general',
    };
    setSuggestedPrompts([...suggestedPrompts, newPrompt]);
  };

  const handleRemovePrompt = (index: number) => {
    setSuggestedPrompts(suggestedPrompts.filter((_, i) => i !== index));
  };

  const handleUpdatePrompt = (
    index: number,
    field: keyof AgentSuggestedPrompt,
    value: string
  ) => {
    const updated = [...suggestedPrompts];
    updated[index] = { ...updated[index], [field]: value };
    setSuggestedPrompts(updated);
  };

  return (
    <div
      className={
        'fixed inset-0 z-50 flex items-center justify-center ' +
        'bg-black bg-opacity-50 p-4'
      }
      onClick={onClose}
      role="dialog"
      aria-labelledby="config-editor-title"
      aria-modal="true"
    >
      <div
        className={
          'relative w-full max-w-3xl rounded-xl bg-bg-primary ' +
          'shadow-2xl max-h-[90vh] overflow-hidden flex flex-col'
        }
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => {
          // Only stop propagation for Escape key to close modal
          // Allow all other keys (including space, letters, etc.) to work normally in inputs
          if (e.key === 'Escape') {
            e.stopPropagation();
            onClose();
          }
        }}
      >
        {/* Header */}
        <div
          className={
            'flex items-center justify-between border-b ' +
            'border-border-default px-6 py-4'
          }
        >
          <div>
            <h2
              id="config-editor-title"
              className="text-xl font-bold text-text-primary"
            >
              Configure Agent
            </h2>
            <p className="mt-1 text-sm text-text-secondary">
              {agent.name} - {agent.icon || '🤖'}
            </p>
          </div>
          <button
            onClick={onClose}
            className={
              'rounded-lg p-2 text-text-tertiary hover:bg-bg-secondary ' +
              'hover:text-text-secondary focus-visible:outline-none ' +
              'focus-visible:ring-2 focus-visible:ring-stellar-primary'
            }
            aria-label="Close configuration editor"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Tabs */}
        <div className="border-b border-border-default bg-bg-secondary px-6">
          <div className="flex gap-1">
            <button
              onClick={() => setActiveTab('details')}
              className={`
                px-3 py-3 text-sm font-medium transition-colors
                border-b-2 -mb-px
                ${
                  activeTab === 'details'
                    ? 'border-stellar-primary text-stellar-primary'
                    : 'border-transparent text-text-secondary hover:text-text-primary'
                }
              `}
              title="Agent Details"
            >
              Details
            </button>
            <button
              onClick={() => setActiveTab('azure')}
              className={`
                px-3 py-3 text-sm font-medium transition-colors
                border-b-2 -mb-px
                ${
                  activeTab === 'azure'
                    ? 'border-stellar-primary text-stellar-primary'
                    : 'border-transparent text-text-secondary hover:text-text-primary'
                }
              `}
              title="Azure AI Foundry Configuration"
            >
              Azure AI
            </button>
            <button
              onClick={() => setActiveTab('prompt')}
              className={`
                px-3 py-3 text-sm font-medium transition-colors
                border-b-2 -mb-px
                ${
                  activeTab === 'prompt'
                    ? 'border-stellar-primary text-stellar-primary'
                    : 'border-transparent text-text-secondary hover:text-text-primary'
                }
              `}
              title="System Prompt Configuration"
            >
              Prompt
            </button>
            <button
              onClick={() => setActiveTab('suggested')}
              className={`
                px-3 py-3 text-sm font-medium transition-colors
                border-b-2 -mb-px flex items-center gap-1.5
                ${
                  activeTab === 'suggested'
                    ? 'border-stellar-primary text-stellar-primary'
                    : 'border-transparent text-text-secondary hover:text-text-primary'
                }
              `}
              title="Suggested Prompts Configuration"
            >
              <Sparkles className="h-4 w-4" />
              Prompts
            </button>
            <button
              onClick={() => setActiveTab('welcome')}
              className={`
                px-3 py-3 text-sm font-medium transition-colors
                border-b-2 -mb-px flex items-center gap-1.5
                ${
                  activeTab === 'welcome'
                    ? 'border-stellar-primary text-stellar-primary'
                    : 'border-transparent text-text-secondary hover:text-text-primary'
                }
              `}
              title="Welcome Message Configuration"
            >
              <MessageSquare className="h-4 w-4" />
              Welcome
            </button>
            <button
              onClick={() => setActiveTab('websearch')}
              className={`
                px-3 py-3 text-sm font-medium transition-colors
                border-b-2 -mb-px flex items-center gap-1.5
                ${
                  activeTab === 'websearch'
                    ? 'border-stellar-primary text-stellar-primary'
                    : 'border-transparent text-text-secondary hover:text-text-primary'
                }
              `}
              title="Web Search Configuration"
            >
              🌐
              Web
            </button>
            <button
              onClick={() => setActiveTab('aisearch')}
              className={`
                px-3 py-3 text-sm font-medium transition-colors
                border-b-2 -mb-px flex items-center gap-1.5
                ${
                  activeTab === 'aisearch'
                    ? 'border-stellar-primary text-stellar-primary'
                    : 'border-transparent text-text-secondary hover:text-text-primary'
                }
              `}
              title="Azure AI Search Configuration"
            >
              <Search className="h-4 w-4" />
              AI Search
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="h-[500px] overflow-y-auto p-6">
          {/* Success Message */}
          {success && (
            <div
              className={
                'mb-4 flex items-center gap-2 rounded-lg ' +
                'bg-success-light p-3 text-success'
              }
            >
              <CheckCircle className="h-5 w-5" />
              <span className="text-sm font-medium">
                Configuration saved successfully!
              </span>
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div
              className={
                'mb-4 flex items-center gap-2 rounded-lg ' +
                'bg-error-light p-3 text-error'
              }
            >
              <AlertCircle className="h-5 w-5" />
              <span className="text-sm font-medium">{error}</span>
            </div>
          )}

          {/* Details Tab */}
          {activeTab === 'details' && (
            <div className="space-y-5">
              <div>
                <h3 className="mb-4 text-lg font-semibold text-text-primary">
                  Agent Details
                </h3>
                <p className="mb-6 text-sm text-text-secondary">
                  Configure the basic information for this agent, including
                  name, description, and icon.
                </p>
              </div>

              {/* Agent Name */}
              <div>
                <label
                  htmlFor="agentName"
                  className="mb-2 block text-sm font-medium text-text-primary"
                >
                  Agent Name
                  <span className="ml-1 text-error">*</span>
                </label>
                <input
                  id="agentName"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="My Custom Agent"
                  className={
                    'w-full rounded-lg border border-border-medium px-4 py-3 ' +
                    'text-sm transition-colors ' +
                    'focus:border-stellar-primary focus:outline-none ' +
                    'focus:ring-2 focus:ring-stellar-primary ' +
                    'focus:ring-opacity-20'
                  }
                  required
                />
                <p className="mt-1.5 text-xs text-text-secondary">
                  A descriptive name for your agent
                </p>
              </div>

              {/* Agent Description */}
              <div>
                <label
                  htmlFor="agentDescription"
                  className="mb-2 block text-sm font-medium text-text-primary"
                >
                  Description
                  <span className="ml-1 text-error">*</span>
                </label>
                <textarea
                  id="agentDescription"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Describe what this agent does and its capabilities..."
                  rows={4}
                  className={
                    'w-full rounded-lg border border-border-medium px-4 py-3 ' +
                    'text-sm transition-colors resize-none ' +
                    'focus:border-stellar-primary focus:outline-none ' +
                    'focus:ring-2 focus:ring-stellar-primary ' +
                    'focus:ring-opacity-20'
                  }
                  required
                />
                <p className="mt-1.5 text-xs text-text-secondary">
                  Explain the agent&apos;s purpose and what users can expect
                </p>
              </div>

              {/* Agent Icon */}
              <div>
                <label
                  htmlFor="agentIcon"
                  className="mb-2 block text-sm font-medium text-text-primary"
                >
                  Agent Icon
                </label>
                <EmojiPicker
                  value={icon}
                  onChange={(emoji) => setIcon(emoji)}
                  placeholder="Select agent icon"
                />
                <p className="mt-1.5 text-xs text-text-secondary">
                  Choose an emoji to represent this agent in the UI
                </p>
              </div>
            </div>
          )}

          {/* Azure AI Foundry Tab */}
          {activeTab === 'azure' && (
            <div className="space-y-5">
              <div>
                <h3 className="mb-4 text-lg font-semibold text-text-primary">
                  Azure AI Foundry Configuration
                </h3>
                <p className="mb-6 text-sm text-text-secondary">
                  Configure the Azure AI Foundry endpoint and deployment
                  settings for this agent. These settings determine which AI
                  model the agent uses for conversations.
                </p>
              </div>

              {/* Endpoint */}
              <div>
                <label
                  htmlFor="endpoint"
                  className="mb-2 block text-sm font-medium text-text-primary"
                >
                  Endpoint URL
                  <span className="ml-1 text-error">*</span>
                </label>
                <input
                  id="endpoint"
                  type="url"
                  value={endpoint}
                  onChange={(e) => setEndpoint(e.target.value)}
                  placeholder="https://your-resource.openai.azure.com"
                  className={
                    'w-full rounded-lg border border-border-medium px-4 py-3 ' +
                    'text-sm transition-colors ' +
                    'focus:border-stellar-primary focus:outline-none ' +
                    'focus:ring-2 focus:ring-stellar-primary ' +
                    'focus:ring-opacity-20'
                  }
                  required
                />
                <p className="mt-1.5 text-xs text-text-secondary">
                  Your Azure OpenAI or AI Foundry resource endpoint
                </p>
              </div>

              {/* API Key */}
              <div>
                <label
                  htmlFor="apiKey"
                  className="mb-2 block text-sm font-medium text-text-primary"
                >
                  API Key
                  <span className="ml-1 text-error">*</span>
                </label>
                <div className="relative">
                  <input
                    id="apiKey"
                    type={showApiKey ? 'text' : 'password'}
                    value={apiKey}
                    onChange={(e) => setApiKey(e.target.value)}
                    placeholder="••••••••••••••••••••••••••••••••"
                    className={
                      'w-full rounded-lg border border-border-medium px-4 ' +
                      'py-3 pr-12 text-sm transition-colors ' +
                      'focus:border-stellar-primary focus:outline-none ' +
                      'focus:ring-2 focus:ring-stellar-primary ' +
                      'focus:ring-opacity-20'
                    }
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowApiKey(!showApiKey)}
                    className={
                      'absolute right-3 top-1/2 -translate-y-1/2 ' +
                      'rounded p-1 text-text-tertiary hover:text-text-secondary'
                    }
                    aria-label={showApiKey ? 'Hide API key' : 'Show API key'}
                  >
                    {showApiKey ? (
                      <EyeOff className="h-4 w-4" />
                    ) : (
                      <Eye className="h-4 w-4" />
                    )}
                  </button>
                </div>
                <p className="mt-1.5 text-xs text-text-secondary">
                  Your Azure OpenAI API key (stored securely)
                </p>
              </div>

              {/* Deployment ID */}
              <div>
                <label
                  htmlFor="deploymentId"
                  className="mb-2 block text-sm font-medium text-text-primary"
                >
                  Deployment ID
                  <span className="ml-1 text-error">*</span>
                </label>
                <input
                  id="deploymentId"
                  type="text"
                  value={deploymentId}
                  onChange={(e) => setDeploymentId(e.target.value)}
                  placeholder="gpt-4, gpt-35-turbo, etc."
                  className={
                    'w-full rounded-lg border border-border-medium px-4 py-3 ' +
                    'text-sm transition-colors ' +
                    'focus:border-stellar-primary focus:outline-none ' +
                    'focus:ring-2 focus:ring-stellar-primary ' +
                    'focus:ring-opacity-20'
                  }
                  required
                />
                <p className="mt-1.5 text-xs text-text-secondary">
                  The name of your deployed model (e.g., gpt-4, gpt-4o)
                </p>
              </div>

              {/* API Version */}
              <div>
                <label
                  htmlFor="apiVersion"
                  className="mb-2 block text-sm font-medium text-text-primary"
                >
                  API Version
                </label>
                <input
                  id="apiVersion"
                  type="text"
                  value={apiVersion}
                  onChange={(e) => setApiVersion(e.target.value)}
                  placeholder="2024-02-15-preview"
                  className={
                    'w-full rounded-lg border border-border-medium px-4 py-3 ' +
                    'text-sm transition-colors ' +
                    'focus:border-stellar-primary focus:outline-none ' +
                    'focus:ring-2 focus:ring-stellar-primary ' +
                    'focus:ring-opacity-20'
                  }
                />
                <p className="mt-1.5 text-xs text-text-secondary">
                  Azure OpenAI API version (defaults to 2024-02-15-preview)
                </p>
              </div>

              {/* Context Window */}
              <div>
                <label
                  htmlFor="contextWindowTokens"
                  className="mb-2 block text-sm font-medium text-text-primary"
                >
                  Context Window (tokens)
                </label>
                <input
                  id="contextWindowTokens"
                  type="number"
                  min={1}
                  step={1000}
                  value={contextWindowTokens}
                  onChange={(e) => setContextWindowTokens(e.target.value)}
                  placeholder="e.g. 128000"
                  className={
                    'w-full rounded-lg border border-border-medium px-4 py-3 ' +
                    'text-sm transition-colors ' +
                    'focus:border-stellar-primary focus:outline-none ' +
                    'focus:ring-2 focus:ring-stellar-primary ' +
                    'focus:ring-opacity-20'
                  }
                />
                <p className="mt-1.5 text-xs text-text-secondary">
                  This deployment&rsquo;s input token limit. The chat trims
                  old history and caps retrieval context to fit, preventing
                  &ldquo;context_length_exceeded&rdquo; errors. Leave blank to
                  use the safe default (120,000). Common windows: gpt-4o
                  128,000; gpt-4.1 / gpt-5 272,000.
                </p>
              </div>

              {/* When the context window is exceeded */}
              <div>
                <label
                  htmlFor="contextOverflowStrategy"
                  className="mb-2 block text-sm font-medium text-text-primary"
                >
                  When the context window is exceeded
                </label>
                <select
                  id="contextOverflowStrategy"
                  value={contextOverflowStrategy}
                  onChange={(e) =>
                    setContextOverflowStrategy(
                      e.target.value as 'trim' | 'summarize'
                    )
                  }
                  className={
                    'w-full rounded-lg border border-border-medium px-4 py-3 ' +
                    'text-sm transition-colors ' +
                    'focus:border-stellar-primary focus:outline-none ' +
                    'focus:ring-2 focus:ring-stellar-primary ' +
                    'focus:ring-opacity-20'
                  }
                >
                  <option value="trim">Trim oldest messages (default)</option>
                  <option value="summarize">
                    Summarize older messages
                  </option>
                </select>
                <p className="mt-1.5 text-xs text-text-secondary">
                  &ldquo;Trim&rdquo; drops the oldest turns. &ldquo;Summarize&rdquo;
                  replaces them with an AI-generated summary (one extra model
                  call when a conversation gets long; falls back to trimming if
                  it fails). Either way the user is notified that earlier
                  context was shortened.
                </p>
              </div>

              {/* Summary model (only relevant when summarizing) */}
              {contextOverflowStrategy === 'summarize' && (
                <div className="rounded-lg border border-border-default bg-bg-secondary p-4">
                  <h3 className="mb-1 text-sm font-semibold text-text-primary">
                    Summary model
                  </h3>
                  <p className="mb-3 text-xs text-text-secondary">
                    Which model generates the summary. Point this at a cheaper
                    deployment (e.g. gpt-4o-mini) to save cost. Only the
                    deployment is required — endpoint, API key, and API version
                    default to this agent&rsquo;s settings when left blank.
                  </p>

                  <div className="space-y-3">
                    <div>
                      <label
                        htmlFor="summaryDeployment"
                        className="mb-1 block text-sm font-medium text-text-primary"
                      >
                        Deployment
                      </label>
                      <input
                        id="summaryDeployment"
                        type="text"
                        value={summaryModel.deployment}
                        onChange={(e) =>
                          setSummaryModel((s) => ({
                            ...s,
                            deployment: e.target.value,
                          }))
                        }
                        placeholder="Leave blank to use this agent's model"
                        className={
                          'w-full rounded-lg border border-border-medium px-4 py-2.5 ' +
                          'text-sm transition-colors focus:border-stellar-primary ' +
                          'focus:outline-none focus:ring-2 focus:ring-stellar-primary ' +
                          'focus:ring-opacity-20'
                        }
                      />
                    </div>

                    <div>
                      <label
                        htmlFor="summaryEndpoint"
                        className="mb-1 block text-sm font-medium text-text-primary"
                      >
                        Endpoint{' '}
                        <span className="text-text-tertiary">(optional)</span>
                      </label>
                      <input
                        id="summaryEndpoint"
                        type="text"
                        value={summaryModel.endpoint}
                        onChange={(e) =>
                          setSummaryModel((s) => ({
                            ...s,
                            endpoint: e.target.value,
                          }))
                        }
                        placeholder="Defaults to this agent's endpoint"
                        className={
                          'w-full rounded-lg border border-border-medium px-4 py-2.5 ' +
                          'text-sm transition-colors focus:border-stellar-primary ' +
                          'focus:outline-none focus:ring-2 focus:ring-stellar-primary ' +
                          'focus:ring-opacity-20'
                        }
                      />
                    </div>

                    <div>
                      <label
                        htmlFor="summaryApiKey"
                        className="mb-1 block text-sm font-medium text-text-primary"
                      >
                        API Key{' '}
                        <span className="text-text-tertiary">(optional)</span>
                      </label>
                      <input
                        id="summaryApiKey"
                        type="password"
                        value={summaryModel.apiKey}
                        onChange={(e) =>
                          setSummaryModel((s) => ({
                            ...s,
                            apiKey: e.target.value,
                          }))
                        }
                        placeholder="Defaults to this agent's API key"
                        className={
                          'w-full rounded-lg border border-border-medium px-4 py-2.5 ' +
                          'text-sm transition-colors focus:border-stellar-primary ' +
                          'focus:outline-none focus:ring-2 focus:ring-stellar-primary ' +
                          'focus:ring-opacity-20'
                        }
                      />
                    </div>

                    <div>
                      <label
                        htmlFor="summaryApiVersion"
                        className="mb-1 block text-sm font-medium text-text-primary"
                      >
                        API Version{' '}
                        <span className="text-text-tertiary">(optional)</span>
                      </label>
                      <input
                        id="summaryApiVersion"
                        type="text"
                        value={summaryModel.apiVersion}
                        onChange={(e) =>
                          setSummaryModel((s) => ({
                            ...s,
                            apiVersion: e.target.value,
                          }))
                        }
                        placeholder="Defaults to this agent's API version"
                        className={
                          'w-full rounded-lg border border-border-medium px-4 py-2.5 ' +
                          'text-sm transition-colors focus:border-stellar-primary ' +
                          'focus:outline-none focus:ring-2 focus:ring-stellar-primary ' +
                          'focus:ring-opacity-20'
                        }
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Reasoning / Advanced Thinking */}
              <div className="border-t border-border-default pt-5">
                <h3 className="mb-1 text-sm font-semibold text-text-primary">
                  Reasoning / Advanced Thinking
                </h3>
                <p className="mb-3 text-xs text-text-secondary">
                  Enable only for reasoning models (o-series, GPT-5) on a
                  2025+ API version. When enabled, users get a per-message
                  &ldquo;Think&rdquo; toggle in the chat input.
                </p>

                <label className="flex items-start gap-2">
                  <input
                    type="checkbox"
                    checked={supportsReasoning}
                    onChange={(e) => setSupportsReasoning(e.target.checked)}
                    className="mt-0.5 h-4 w-4 rounded border-border-medium text-stellar-primary focus:ring-stellar-primary"
                  />
                  <span className="text-sm text-text-primary">
                    This deployment is a reasoning model
                  </span>
                </label>

                {supportsReasoning && (
                  <div className="mt-4 space-y-4 pl-6">
                    <div>
                      <label
                        htmlFor="reasoningOnEffort"
                        className="mb-2 block text-sm font-medium text-text-primary"
                      >
                        Effort when &ldquo;Think&rdquo; is on
                      </label>
                      <select
                        id="reasoningOnEffort"
                        value={reasoningOnEffort}
                        onChange={(e) =>
                          setReasoningOnEffort(
                            e.target.value as ReasoningEffort
                          )
                        }
                        className={
                          'w-full rounded-lg border border-border-medium px-4 py-3 ' +
                          'text-sm transition-colors ' +
                          'focus:border-stellar-primary focus:outline-none ' +
                          'focus:ring-2 focus:ring-stellar-primary ' +
                          'focus:ring-opacity-20'
                        }
                      >
                        <option value="minimal">Minimal</option>
                        <option value="low">Low</option>
                        <option value="medium">Medium</option>
                        <option value="high">High</option>
                      </select>
                      <p className="mt-1.5 text-xs text-text-secondary">
                        Sent as <code>reasoning_effort</code>. When the toggle
                        is off, no reasoning parameter is sent (model default).
                      </p>
                    </div>

                    <label className="flex items-start gap-2">
                      <input
                        type="checkbox"
                        checked={reasoningDefaultEnabled}
                        onChange={(e) =>
                          setReasoningDefaultEnabled(e.target.checked)
                        }
                        className="mt-0.5 h-4 w-4 rounded border-border-medium text-stellar-primary focus:ring-stellar-primary"
                      />
                      <span className="text-sm text-text-primary">
                        Turn the toggle on by default in new sessions
                      </span>
                    </label>
                  </div>
                )}
              </div>

              {/* Image input (Vision) */}
              <div className="border-t border-border-default pt-5">
                <h3 className="mb-1 text-sm font-semibold text-text-primary">
                  Image Input (Vision)
                </h3>
                <p className="mb-3 text-xs text-text-secondary">
                  Enable only if this deployment uses a vision-capable model
                  (GPT-5, GPT-4o, GPT-4.1). When enabled, users can attach
                  images (PNG, JPEG, WEBP, GIF) to a message.
                </p>
                <label className="flex items-start gap-2">
                  <input
                    type="checkbox"
                    checked={supportsVision}
                    onChange={(e) => setSupportsVision(e.target.checked)}
                    className="mt-0.5 h-4 w-4 rounded border-border-medium text-stellar-primary focus:ring-stellar-primary"
                  />
                  <span className="text-sm text-text-primary">
                    This deployment supports image input
                  </span>
                </label>
              </div>
            </div>
          )}

          {/* System Prompt Tab */}
          {activeTab === 'prompt' && (
            <div className="space-y-5">
              <div>
                <h3 className="mb-4 text-lg font-semibold text-text-primary">
                  System Prompt Configuration
                </h3>
                <p className="mb-6 text-sm text-text-secondary">
                  The system prompt defines your agent&apos;s behavior,
                  personality, and capabilities. It&apos;s sent with every
                  conversation to guide the AI&apos;s responses.
                </p>
              </div>

              {/* System Prompt Textarea */}
              <div>
                <div className="mb-2 flex items-center justify-between">
                  <label
                    htmlFor="systemPrompt"
                    className="block text-sm font-medium text-text-primary"
                  >
                    System Prompt
                    <span className="ml-1 text-error">*</span>
                  </label>
                  <span className="text-xs text-text-secondary">
                    {systemPrompt.length} characters
                  </span>
                </div>
                <textarea
                  id="systemPrompt"
                  value={systemPrompt}
                  onChange={(e) => setSystemPrompt(e.target.value)}
                  className={
                    'w-full h-64 rounded-lg border border-border-medium px-4 py-3 ' +
                    'font-mono text-sm transition-colors resize-none ' +
                    'overflow-y-auto ' +
                    'focus:border-stellar-primary focus:outline-none ' +
                    'focus:ring-2 focus:ring-stellar-primary ' +
                    'focus:ring-opacity-20'
                  }
                  placeholder="Enter your system prompt here..."
                  required
                />
              </div>

              {/* Tips */}
              <div className="rounded-lg bg-bg-tertiary p-4">
                <h4 className="mb-2 text-sm font-semibold text-text-primary">
                  💡 Tips for Effective System Prompts
                </h4>
                <ul className="space-y-1 text-xs text-text-primary">
                  <li>
                    • Be specific about the agent&apos;s role and expertise
                  </li>
                  <li>• Define response format (e.g., use markdown)</li>
                  <li>
                    • Set boundaries on what the agent can and cannot do
                  </li>
                  <li>• Include citation requirements for M365 agents</li>
                  <li>
                    • Keep it concise but comprehensive (200-500 words ideal)
                  </li>
                </ul>
              </div>

              {/* Reset Button */}
              <div className="flex justify-end">
                <button
                  type="button"
                  onClick={handleResetPrompt}
                  className={
                    'rounded-lg border border-border-medium px-4 py-2 ' +
                    'text-sm font-medium text-text-primary ' +
                    'hover:bg-bg-secondary ' +
                    'focus-visible:outline-none focus-visible:ring-2 ' +
                    'focus-visible:ring-stellar-primary'
                  }
                >
                  Reset to Default
                </button>
              </div>
            </div>
          )}

          {/* Suggested Prompts Tab */}
          {activeTab === 'suggested' && (
            <div className="space-y-5">
              <div>
                <h3 className="text-lg font-semibold text-text-primary">
                  Suggested Prompts
                </h3>
                <p className="mb-6 text-sm text-text-secondary">
                  Configure quick-start prompts that users will see when
                  starting a new conversation with this agent. These help users
                  discover the agent&apos;s capabilities.
                </p>
              </div>

              {/* Prompts List */}
              <div className="space-y-4">
                {suggestedPrompts.map((prompt, index) => (
                  <div
                    key={index}
                    className="rounded-lg border-2 border-border-default bg-bg-primary p-4"
                  >
                    <div className="mb-3 flex items-start justify-between">
                      <span className="text-sm font-medium text-text-secondary">
                        Prompt #{index + 1}
                      </span>
                      <button
                        type="button"
                        onClick={() => handleRemovePrompt(index)}
                        className="text-error hover:text-error"
                        aria-label="Remove prompt"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>

                    <div className="grid gap-4 md:grid-cols-2">
                      {/* Icon */}
                      <div>
                        <label className="mb-1 block text-xs font-medium text-text-primary">
                          Icon (Emoji)
                        </label>
                        <EmojiPicker
                          value={prompt.icon || ''}
                          onChange={(emoji) =>
                            handleUpdatePrompt(index, 'icon', emoji)
                          }
                          placeholder="Select emoji"
                        />
                      </div>

                      {/* Category */}
                      <div>
                        <label className="mb-1 block text-xs font-medium text-text-primary">
                          Category
                        </label>
                        <input
                          type="text"
                          value={prompt.category || ''}
                          onChange={(e) =>
                            handleUpdatePrompt(index, 'category', e.target.value)
                          }
                          className="w-full rounded-lg border border-border-medium px-3 py-2 text-sm"
                          placeholder="general"
                        />
                      </div>
                    </div>

                    {/* Title */}
                    <div className="mt-3">
                      <label className="mb-1 block text-xs font-medium text-text-primary">
                        Title
                      </label>
                      <input
                        type="text"
                        value={prompt.title}
                        onChange={(e) =>
                          handleUpdatePrompt(index, 'title', e.target.value)
                        }
                        className="w-full rounded-lg border border-border-medium px-3 py-2 text-sm"
                        placeholder="Enter a short title"
                        required
                      />
                    </div>

                    {/* Prompt Text */}
                    <div className="mt-3">
                      <label className="mb-1 block text-xs font-medium text-text-primary">
                        Prompt Text
                      </label>
                      <textarea
                        value={prompt.prompt}
                        onChange={(e) =>
                          handleUpdatePrompt(index, 'prompt', e.target.value)
                        }
                        className="w-full h-20 rounded-lg border border-border-medium px-3 py-2 text-sm resize-none"
                        placeholder="Enter the full prompt text that will be sent to the AI"
                        required
                      />
                    </div>
                  </div>
                ))}
              </div>

              {/* Add Prompt Button */}
              <div>
                <button
                  type="button"
                  onClick={handleAddPrompt}
                  className="flex w-full items-center justify-center gap-2 rounded-lg border-2 border-dashed border-border-medium py-4 text-sm font-medium text-text-secondary hover:border-stellar-primary hover:text-stellar-primary"
                >
                  <Plus className="h-4 w-4" />
                  Add Suggested Prompt
                </button>
              </div>

              {/* Tips */}
              <div className="rounded-lg bg-bg-tertiary p-4">
                <h4 className="mb-2 text-sm font-semibold text-text-primary">
                  💡 Tips for Suggested Prompts
                </h4>
                <ul className="space-y-1 text-xs text-text-primary">
                  <li>• Keep titles short and descriptive (3-5 words)</li>
                  <li>
                    • Make prompts specific to the agent&apos;s capabilities
                  </li>
                  <li>• Use clear, actionable language</li>
                  <li>• Aim for 6-12 prompts per agent for variety</li>
                  <li>
                    • Group similar prompts with consistent categories
                  </li>
                </ul>
              </div>
            </div>
          )}

          {/* Welcome Messages Tab */}
          {activeTab === 'welcome' && (
            <div className="space-y-5">
              <div>
                <h3 className="text-lg font-semibold text-text-primary">
                  Welcome Messages
                </h3>
                <p className="mb-6 text-sm text-text-secondary">
                  Customize the welcome message shown when users start a new
                  conversation with this agent. If disabled, the global admin
                  welcome message will be used instead.
                </p>
              </div>

              {/* Use Custom Message Toggle */}
              <div className="rounded-lg border-2 border-border-default bg-bg-primary p-4">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={welcomeMessage.useCustomMessage}
                    onChange={(e) =>
                      setWelcomeMessage({
                        ...welcomeMessage,
                        useCustomMessage: e.target.checked,
                      })
                    }
                    className="h-4 w-4 rounded border-border-medium text-stellar-primary focus:ring-stellar-primary"
                  />
                  <div>
                    <span className="text-sm font-medium text-text-primary">
                      Use Custom Welcome Message for this Agent
                    </span>
                    <p className="text-xs text-text-secondary mt-1">
                      When enabled, this agent will show a custom welcome
                      message. When disabled, the global admin welcome message
                      will be displayed.
                    </p>
                  </div>
                </label>
              </div>

              {/* Custom Welcome Message Editors */}
              {welcomeMessage.useCustomMessage && (
                <div className="space-y-4">
                  {/* Title Editor */}
                  <div>
                    <MarkdownEditor
                      label="Welcome Title"
                      value={welcomeMessage.title}
                      onChange={(value) =>
                        setWelcomeMessage({
                          ...welcomeMessage,
                          title: value,
                        })
                      }
                      placeholder={`Welcome to ${agent.name}`}
                      minHeight="80px"
                    />
                  </div>

                  {/* Subtitle Editor */}
                  <div>
                    <MarkdownEditor
                      label="Welcome Subtitle"
                      value={welcomeMessage.subtitle}
                      onChange={(value) =>
                        setWelcomeMessage({
                          ...welcomeMessage,
                          subtitle: value,
                        })
                      }
                      placeholder="Select a suggested prompt below or type your question"
                      minHeight="100px"
                    />
                  </div>

                  {/* Preview */}
                  <div className="rounded-lg border-2 border-stellar-primary-light bg-stellar-primary-light/10 p-4">
                    <div className="text-sm font-medium text-text-secondary mb-2">
                      Preview:
                    </div>
                    <div className="text-xl font-bold text-text-primary mb-2">
                      {welcomeMessage.title || `Welcome to ${agent.name}`}
                    </div>
                    <div className="text-text-secondary">
                      {welcomeMessage.subtitle ||
                        'Select a suggested prompt below or type your question'}
                    </div>
                  </div>
                </div>
              )}

              {/* Info when custom message is disabled */}
              {!welcomeMessage.useCustomMessage && (
                <div className="rounded-lg bg-bg-tertiary p-4">
                  <h4 className="mb-2 text-sm font-semibold text-text-primary">
                    ℹ️ Using Global Welcome Message
                  </h4>
                  <p className="text-xs text-text-primary">
                    This agent will display the global welcome message
                    configured by the administrator. Enable the custom message
                    option above to create an agent-specific welcome message.
                  </p>
                </div>
              )}

              {/* Tips */}
              <div className="rounded-lg bg-bg-tertiary p-4">
                <h4 className="mb-2 text-sm font-semibold text-text-primary">
                  💡 Tips for Welcome Messages
                </h4>
                <ul className="space-y-1 text-xs text-text-primary">
                  <li>
                    • Keep the title short and engaging (1-2 lines)
                  </li>
                  <li>
                    • Use the subtitle to guide users on how to interact
                  </li>
                  <li>
                    • Markdown formatting: **bold**, *italic*, [links](url)
                  </li>
                  <li>
                    • Mention the agent&apos;s specific capabilities or focus
                  </li>
                  <li>
                    • Reference suggested prompts to encourage exploration
                  </li>
                </ul>
              </div>
            </div>
          )}

          {/* Web Search Tab */}
          {activeTab === 'websearch' && (
            <div className="space-y-5">
              <div>
                <h3 className="text-lg font-semibold text-text-primary">
                  Web Search Configuration
                </h3>
                <p className="mb-6 text-sm text-text-secondary">
                  Enable web search for this agent using external search APIs.
                  Results will be included in the AI&apos;s context for more informed responses.
                </p>
              </div>

              {/* Enable Web Search Toggle */}
              <div className="rounded-lg border-2 border-border-default bg-bg-primary p-4">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={webSearchConfig.enabled}
                    onChange={(e) =>
                      setWebSearchConfig({
                        ...webSearchConfig,
                        enabled: e.target.checked,
                      } as AgentWebSearchConfig)
                    }
                    className="h-4 w-4 rounded border-border-medium text-stellar-primary focus:ring-stellar-primary"
                  />
                  <div>
                    <span className="text-sm font-medium text-text-primary">
                      Enable Web Search
                    </span>
                    <p className="text-xs text-text-secondary mt-1">
                      When enabled, this agent will search the web using the selected provider
                      to enhance responses with real-time information.
                    </p>
                  </div>
                </label>
              </div>

              {/* Provider Selection and Configuration */}
              {webSearchConfig.enabled && (
                <>
                  <WebSearchDefaultStateField
                    defaultEnabled={webSearchConfig.defaultEnabled}
                    onChange={(defaultEnabled) =>
                      setWebSearchConfig({ ...webSearchConfig, defaultEnabled })
                    }
                  />
                  {/* Provider Dropdown */}
                  <div>
                    <label className="mb-2 block text-sm font-medium text-text-primary">
                      Search Provider
                      <span className="ml-1 text-error">*</span>
                    </label>
                    <select
                      value={webSearchConfig.provider}
                      onChange={(e) => {
                        const provider = e.target.value as WebSearchProvider;
                        // Reset config when provider changes
                        setWebSearchConfig({
                          enabled: true,
                          provider,
                          maxResults: 5,
                        } as AgentWebSearchConfig);
                      }}
                      className="w-full rounded-lg border border-border-medium px-4 py-3 text-sm transition-colors focus:border-stellar-primary focus:outline-none focus:ring-2 focus:ring-stellar-primary focus:ring-opacity-20"
                    >
                      <option value="none">-- Select Provider --</option>
                      <option value="tavily">Tavily (LLM-optimized, includes AI answer)</option>
                      <option value="bing">Bing Web Search (Azure)</option>
                      <option value="google">Google Custom Search</option>
                      <option value="serpapi">SerpAPI (Google/Bing scraping)</option>
                      <option value="brave">Brave Search (Privacy-focused)</option>
                    </select>
                  </div>

                  {/* Common Settings: Max Results */}
                  {(webSearchConfig as { provider: WebSearchProvider }).provider !== 'none' && (
                    <div>
                      <label className="mb-2 block text-sm font-medium text-text-primary">
                        Maximum Results
                      </label>
                      <input
                        type="number"
                        min="1"
                        max="25"
                        value={webSearchConfig.maxResults || 5}
                        onChange={(e) =>
                          setWebSearchConfig({
                            ...webSearchConfig,
                            maxResults: parseInt(e.target.value),
                          })
                        }
                        className="w-full rounded-lg border border-border-medium px-4 py-3 text-sm focus:border-stellar-primary focus:outline-none"
                      />
                      <p className="mt-1.5 text-xs text-text-secondary">
                        Number of search results to include in context (1-25)
                      </p>
                    </div>
                  )}

                  {/* Provider-Specific Configuration */}
                  {webSearchConfig.provider === 'tavily' && (
                    <TavilyConfigForm
                      config={webSearchConfig as TavilySearchConfig}
                      onChange={(updates) =>
                        setWebSearchConfig({ ...webSearchConfig, ...updates })
                      }
                    />
                  )}

                  {webSearchConfig.provider === 'bing' && (
                    <BingConfigForm
                      config={webSearchConfig as BingSearchConfig}
                      onChange={(updates) =>
                        setWebSearchConfig({ ...webSearchConfig, ...updates })
                      }
                    />
                  )}

                  {webSearchConfig.provider === 'google' && (
                    <GoogleConfigForm
                      config={webSearchConfig as GoogleSearchConfig}
                      onChange={(updates) =>
                        setWebSearchConfig({ ...webSearchConfig, ...updates })
                      }
                    />
                  )}

                  {webSearchConfig.provider === 'serpapi' && (
                    <SerpApiConfigForm
                      config={webSearchConfig as SerpApiSearchConfig}
                      onChange={(updates) =>
                        setWebSearchConfig({ ...webSearchConfig, ...updates })
                      }
                    />
                  )}

                  {webSearchConfig.provider === 'brave' && (
                    <BraveConfigForm
                      config={webSearchConfig as BraveSearchConfig}
                      onChange={(updates) =>
                        setWebSearchConfig({ ...webSearchConfig, ...updates })
                      }
                    />
                  )}
                </>
              )}

              {/* Tips */}
              <div className="rounded-lg bg-bg-tertiary p-4">
                <h4 className="mb-2 text-sm font-semibold text-text-primary">
                  💡 Web Search Tips
                </h4>
                <ul className="space-y-1 text-xs text-text-primary">
                  <li>• Web search runs on every user query when enabled</li>
                  <li>• Results are added to the AI&apos;s context automatically</li>
                  <li>• Tavily includes AI-generated answer summaries</li>
                  <li>• Bing requires Azure subscription (recommended for Azure deployments)</li>
                  <li>• Google requires custom search engine setup</li>
                  <li>• Consider API rate limits and costs per query</li>
                </ul>
              </div>
            </div>
          )}

          {/* AI Search Tab */}
          {activeTab === 'aisearch' && (
            <div className="space-y-5">
              <div>
                <h3 className="text-lg font-semibold text-text-primary">
                  Azure AI Search Configuration
                </h3>
                <p className="mb-6 text-sm text-text-secondary">
                  Configure Azure AI Search to query pre-indexed documents from
                  Office, OneDrive, and SharePoint. This provides semantic
                  search with ranking powered by Azure OpenAI.
                </p>
              </div>

              {/* Enable AI Search Toggle */}
              <div className="rounded-lg border-2 border-border-default bg-bg-primary p-4">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={aiSearchConfig.enabled}
                    onChange={(e) =>
                      setAISearchConfig({
                        ...aiSearchConfig,
                        enabled: e.target.checked,
                      } as AzureAISearchConfig)
                    }
                    className="h-4 w-4 rounded border-border-medium text-stellar-primary focus:ring-stellar-primary"
                  />
                  <div>
                    <span className="text-sm font-medium text-text-primary">
                      Enable Azure AI Search
                    </span>
                    <p className="text-xs text-text-secondary mt-1">
                      When enabled, this agent will search pre-indexed
                      documents using Azure AI Search for more accurate and
                      faster results than real-time Graph API search.
                    </p>
                  </div>
                </label>
              </div>

              {/* AI Search Configuration Form */}
              {aiSearchConfig.enabled && (
                <AISearchConfigForm
                  config={aiSearchConfig}
                  onChange={(updates) =>
                    setAISearchConfig({ ...aiSearchConfig, ...updates })
                  }
                />
              )}

              {/* Tips */}
              <div className="rounded-lg bg-bg-tertiary p-4">
                <h4 className="mb-2 text-sm font-semibold text-text-primary">
                  💡 Azure AI Search Tips
                </h4>
                <ul className="space-y-1 text-xs text-text-primary">
                  <li>• Requires pre-indexed Azure AI Search service with Office documents</li>
                  <li>• Provides faster results than real-time Microsoft Graph API search</li>
                  <li>• Supports semantic ranking with Azure OpenAI for better relevance</li>
                  <li>• Enable vector search for hybrid keyword + semantic similarity</li>
                  <li>• Search priority: AI Search → Graph API → Web Search</li>
                  <li>• Results include citations with document titles and URLs</li>
                </ul>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div
          className={
            'flex items-center justify-between border-t ' +
            'border-border-default bg-bg-secondary px-6 py-4'
          }
        >
          <div className="flex gap-3">
            {hasChanges && (
              <button
                type="button"
                onClick={handleRevert}
                disabled={isSaving}
                className={
                  'rounded-lg border border-border-medium px-4 py-2 ' +
                  'text-sm font-medium text-text-primary ' +
                  'hover:bg-bg-secondary disabled:cursor-not-allowed ' +
                  'disabled:opacity-50 ' +
                  'focus-visible:outline-none focus-visible:ring-2 ' +
                  'focus-visible:ring-stellar-primary'
                }
              >
                Revert Changes
              </button>
            )}
          </div>

          <div className="flex gap-3">
            <button
              type="button"
              onClick={onClose}
              disabled={isSaving}
              className={
                'rounded-lg border border-border-medium px-4 py-2 ' +
                'text-sm font-medium text-text-primary ' +
                'hover:bg-bg-secondary disabled:cursor-not-allowed ' +
                'disabled:opacity-50 ' +
                'focus-visible:outline-none focus-visible:ring-2 ' +
                'focus-visible:ring-stellar-primary'
              }
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={!hasChanges || isSaving}
              className={
                'flex items-center gap-2 rounded-lg ' +
                'bg-stellar-primary px-4 py-2 text-sm font-medium ' +
                'text-white hover:bg-stellar-primary-dark ' +
                'disabled:cursor-not-allowed disabled:opacity-50 ' +
                'focus-visible:outline-none focus-visible:ring-2 ' +
                'focus-visible:ring-stellar-primary ' +
                'focus-visible:ring-offset-2'
              }
            >
              {isSaving ? (
                <>
                  <div
                    className={
                      'h-4 w-4 animate-spin rounded-full ' +
                      'border-2 border-white border-t-transparent'
                    }
                  />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4" />
                  Save Configuration
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
